import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { OpeningActions } from '@src/components/OpeningActions';
import { createLink } from '@src/util/createLink';
import { isDraft, isInactive } from '@src/util/opening';
import { OpeningActionsButtons } from '@src/components/OpeningActionsButtons';
import { OpeningSourceEnum, useGetTaActiveOpeningQuery } from '@src/graphql/generated';
import { ActionMenuPopover } from '@src/components/Popover';
import { MetricContext } from '@src/metrics/metrics.enum';
import { FUNNEL_OVERVIEW_ROUTE, FUNNEL_ROUTE, OLD_FUNNEL_ROUTE } from '@src/Routes/links';
import { Skeleton } from '../Skeleton';
import { BackButton } from '../BackButton';
import { OpeningParam } from './interfaces/opening.interface';
import { OpeningHeaderProps } from './props';
import './styles.scss';

export const OpeningHeader: FC<OpeningHeaderProps> = ({
  activeOpeningId,
  isActiveOpeningLoading,
  isApplicantsLinkHidden,
}) => {
  const {
    data: openingData,
    loading: isLoading,
    updateQuery: updateOpeningQuery,
  } = useGetTaActiveOpeningQuery({
    variables: {
      id: activeOpeningId,
    },
  });

  const opening = {
    ...openingData?.opening,
    title: openingData?.opening.title || undefined,
  };

  const updateOpening = (opening: OpeningParam) => {
    updateOpeningQuery(data => ({
      ...data,
      opening: {
        ...data.opening,
        state: opening.state || data.opening.state,
      },
    }));
  };

  const headerButtonActions = opening?.state && isInactive(opening.state) ? [] : ['republish'];

  if (isActiveOpeningLoading || isLoading) {
    return <Skeleton />;
  }

  return (
    <div className="pipeline-talents-opening-header fade-in">
      <div className="pipeline-talents-opening-header__back">
        <BackButton />
      </div>

      <div className="pipeline-talents-opening-header__title">{opening?.title}</div>

      <div className="pipeline-talents-opening-header__links">
        {!isApplicantsLinkHidden && !isDraft(opening?.state) && (
          <Link
            className="pipeline-talents-opening-header__list-item"
            to={createLink({
              link: FUNNEL_ROUTE,
              params: { openingId: activeOpeningId },
            })}
          >
            View applicants
          </Link>
        )}

        <Link
          className="pipeline-talents-opening-header__list-item"
          to={createLink({
            link: isDraft(opening?.state) ? OLD_FUNNEL_ROUTE : FUNNEL_OVERVIEW_ROUTE,
            params: { openingId: activeOpeningId },
          })}
        >
          View details
        </Link>
      </div>

      <div className="pipeline-talents-opening-header__divider" />

      {opening?.state && (
        <div className="pipeline-talents-opening-header__actions-wrapper">
          <OpeningActions
            actionSelection={headerButtonActions}
            metricsPrevContext={MetricContext.SOURCE}
            opening={opening}
            updateOpening={updateOpening}
          >
            {({ actions, handleAction }) =>
              opening?.state ? (
                <OpeningActionsButtons actions={actions} onAction={handleAction} openingState={opening.state} />
              ) : (
                <div />
              )
            }
          </OpeningActions>

          {opening.source === OpeningSourceEnum.MEETFRANK && (
            <span className="pipeline-talents-opening-header__actions-wrapper-menu">
              <OpeningActions metricsPrevContext={MetricContext.SOURCE} opening={opening} updateOpening={updateOpening}>
                {({ actions, handleAction }) => <ActionMenuPopover actions={actions} onAction={handleAction} />}
              </OpeningActions>
            </span>
          )}
        </div>
      )}
    </div>
  );
};
